/**
 * 공용 라벨 매핑 — 개발용 enum → 사용자 자연어.
 *
 * 원칙:
 * - 전역 한 곳에 모아 admin/client/superadmin 이 공유.
 * - 알 수 없는 값은 원문(enum) 그대로 반환 (문자열 치환 실패가 빈 화면으로 이어지지 않게).
 * - i18n 도입 시 이 파일만 JSON 기반으로 리팩터.
 */

export type AttendanceTypeLike = 'CHECKIN' | 'CHECKOUT' | 'OUT' | 'RETURN' | 'ABSENCE' | string
export type AttendanceStatusLike = 'NORMAL' | 'LATE' | 'EARLY_LEAVE' | 'ABSENT' | 'UNCHECKOUT' | string
export type ManualReasonLike =
  | 'NFC_ERROR'
  | 'NO_CARD'
  | 'BROKEN_DEVICE'
  | 'APP_ERROR'
  | 'ETC'
  | 'ADMIN_ADDED'
  // 부재 사유 (§16-D16, 5/21) — 별도 absenceLabel 컬럼 없이 manualReason 으로 통합
  | 'VACATION'
  | 'BUSINESS_TRIP'
  | 'EDUCATION'
  | 'OTHER'
  | 'OUTSIDE'
  | 'REMOTE'
  | string

/** 부재 사유 enum 목록 (§16-D16). UI group-by·필터는 이 배열 기준 동적 렌더링 — enum별 하드코딩 금지.
 *  2026-06-08 DCMC 요청 — OUTSIDE(외출) 제거, OTHER(기타) 추가. 본원에 외출 없음.
 *  OUTSIDE 는 detection 에서도 빠짐(기존 외출 기록은 출근/퇴근으로 표시) — 라벨/이력만 MANUAL_REASON_MAP 에 잔존.
 *  OTHER(부재 기타) 는 ETC(수기 기타)와 다른 코드 — 단일 manualReason 필드 충돌 방지. */
export const ABSENCE_REASONS = ['VACATION', 'BUSINESS_TRIP', 'EDUCATION', 'OTHER'] as const
export type ManualStatusLike = 'PENDING' | 'APPROVED' | 'REJECTED' | string

const ATTENDANCE_TYPE_MAP: Record<string, string> = {
  CHECKIN: '출근',
  CHECKOUT: '퇴근',
  OUT: '외출',
  RETURN: '복귀',
  ABSENCE: '부재', // 부재 record fallback (보통 attendanceDisplayType→manualReasonLabel 로 휴가/출장 표기)
}

const ATTENDANCE_STATUS_MAP: Record<string, string> = {
  NORMAL: '정상',
  LATE: '지각',
  EARLY_LEAVE: '조퇴',
  ABSENT: '미출근', // DCMC Q25: "결근" → "미출근" 으로 전 조직 통일
  UNCHECKOUT: '미퇴근',
}

const MANUAL_REASON_MAP: Record<string, string> = {
  NFC_ERROR: 'NFC 불량',
  NO_CARD: '단말 미지참',
  BROKEN_DEVICE: '단말 고장',
  APP_ERROR: '앱 오류',
  ETC: '기타',
  ADMIN_ADDED: '관리자 추가',
  // 부재 사유 (§16-D16, 5/21)
  VACATION: '휴가',
  BUSINESS_TRIP: '출장',
  EDUCATION: '교육',
  // 부재 사유 — 기타 (2026-06-08 DCMC 요청, 외출 대체). ETC(수기 기타)와 다른 코드.
  OTHER: '기타',
  // 이력 호환 (DCMC 이전 선택지) — 외출은 2026-06-08 제거됐으나 옛 기록 라벨용 유지
  OUTSIDE: '외출',
  REMOTE: '재택',
}

const MANUAL_STATUS_MAP: Record<string, string> = {
  PENDING: '승인 대기',
  APPROVED: '승인됨',
  REJECTED: '반려됨',
}

const SHIFT_MODE_MAP: Record<string, string> = {
  FIXED: '고정 근무',
  ROTATION: '교대 근무',
}

const SESSION_STATUS_MAP: Record<string, string> = {
  OPEN: '진행 중',
  CLOSED: '종료',
  AUTO_CLOSED: '자동 종료',
  CANCELLED: '취소됨',
}

/** CHECKIN → "출근". 미지값은 원문 반환. */
export function attendanceTypeLabel(type: AttendanceTypeLike | null | undefined): string {
  if (!type) return ''
  return ATTENDANCE_TYPE_MAP[type] ?? type
}

/** NORMAL → "정상". 라이트 v0.5: BASIC은 NORMAL만 INSERT, LATE/EARLY_LEAVE/ABSENT는 옛 데이터 표시용. */
export function attendanceStatusLabel(
  status: AttendanceStatusLike | null | undefined,
): string {
  if (!status) return ''
  return ATTENDANCE_STATUS_MAP[status] ?? status
}

/** NFC_ERROR → "NFC 태그 오류". */
export function manualReasonLabel(reason: ManualReasonLike | null | undefined): string {
  if (!reason) return ''
  return MANUAL_REASON_MAP[reason] ?? reason
}

/**
 * 출퇴근 "유형" 표시 라벨 — 부재(휴가/출장/교육/외출)는 type(CHECKIN) 대신 부재 사유로 표시.
 *   부재는 종일 placeholder(09:00 CHECKIN)라 "출근"으로 보이면 안 됨 (§16-D16).
 *   REJECTED 부재는 부재로 보지 않음(반려). 그 외(APPROVED·NONE·PENDING)는 부재 라벨.
 * 모든 화면·엑셀 export 공용 — per-record type 을 보여주는 곳은 이 함수를 써야 부재가 출근으로 안 샌다.
 */
export function attendanceDisplayType(rec: {
  type?: AttendanceTypeLike | null
  manualReason?: string | null
  manualStatus?: string | null
}): string {
  if (isAbsenceRecord(rec)) {
    return manualReasonLabel(rec.manualReason)
  }
  return attendanceTypeLabel(rec.type)
}

/**
 * 부재(휴가/출장/교육/외출) 레코드 판별 — 반려(REJECTED)는 제외.
 *   부재는 종일 placeholder(09:00 CHECKIN)라 시각 표시가 무의미 → "종일"로 표기하기 위한 공용 판별 (추가작업 F).
 *   PENDING 부재도 포함 (승인 대기 부재도 "종일"로 일관 표기).
 */
export function isAbsenceRecord(rec: {
  type?: AttendanceTypeLike | null
  manualReason?: string | null
  manualStatus?: string | null
}): boolean {
  // 반려(REJECTED)는 부재로 보지 않음 (신규 type=ABSENCE 도 동일).
  if (rec.manualStatus === 'REJECTED') return false
  // 신규: 부재 전용 타입 (2026-06-11 type=ABSENCE 도입).
  if (rec.type === 'ABSENCE') return true
  // 레거시: type=CHECKIN placeholder + manualReason 으로 저장된 기존 부재.
  return (
    rec.manualReason != null &&
    (ABSENCE_REASONS as readonly string[]).includes(rec.manualReason)
  )
}

/** PENDING → "승인 대기". */
export function manualStatusLabel(status: ManualStatusLike | null | undefined): string {
  if (!status) return ''
  return MANUAL_STATUS_MAP[status] ?? status
}

/** FIXED → "고정 근무" / ROTATION → "교대 근무". */
export function shiftModeLabel(mode: string | null | undefined): string {
  if (!mode) return ''
  return SHIFT_MODE_MAP[mode] ?? mode
}

/** OPEN → "진행 중" / CLOSED → "종료" 등. */
export function sessionStatusLabel(status: string | null | undefined): string {
  if (!status) return ''
  return SESSION_STATUS_MAP[status] ?? status
}

/**
 * 감사 로그 action 코드 → 자연어 라벨.
 * 모노스페이스 코드처럼 보이지 않게 사용자 노출용 한글 매핑.
 * 미정의 액션은 원문 그대로 (디버깅 용도).
 */
const AUDIT_ACTION_MAP: Record<string, string> = {
  // 출퇴근
  'attendance.update': '출퇴근 기록 수정',
  'attendance.deleteOne': '출퇴근 기록 삭제',
  'attendance.adminDelete': '출퇴근 기록 일괄 삭제',
  'attendance.manualApprove': '수기 신청 승인',
  'attendance.manualReject': '수기 신청 반려',
  // 직원
  'employee.create': '직원 등록',
  'employee.update': '직원 정보 수정',
  'employee.resign': '직원 퇴사 처리',
  'employee.restore': '직원 복구',
  'employee.inviteGenerated': '초대 링크 발급',
  // 기기
  'device.approve': '기기 등록 승인',
  'device.reject': '기기 등록 반려',
  // 해제 = 이미 쓰던 기기의 태깅 권한을 뺏는 것. 승인·반려와 성격이 달라 말을 나눈다
  'device.revoke': '기기 사용 해제',
  // 교육 증빙 — 열람까지 남긴다. 표에 viewedAt 칸을 안 둔 것과 안 부딪힌다
  // (칸은 상태라 배지·알림으로 자라고, 로그는 흘러간 사실이라 자랄 자리가 없다)
  'training.upload': '교육 증빙 등록',
  'training.view': '교육 증빙 열람',
  'training.delete': '교육 증빙 삭제',
  // 조직 설정 — 판정 모드는 계약·노사 분쟁과 직결되므로 별도 액션으로 남긴다
  'organization.judgmentMode': '지각·조퇴 표시 설정 변경',
  // 비밀번호
  'password.selfReset': '비밀번호 셀프 리셋',
  'password.tempIssue': '임시 비밀번호 발급',
  'password.adminReset': '관리자 비밀번호 재설정', // 폐기 액션 (이력 호환)
  'passwordReset.approve': '비밀번호 리셋 승인', // 폐기 액션
  'passwordReset.reject': '비밀번호 리셋 반려', // 폐기 액션
  // NFC
  'nfcSpot.create': 'NFC 태그판 추가',
  'nfcSpot.update': 'NFC 태그판 수정',
  'nfcSpot.delete': 'NFC 태그판 삭제',
  'nfcMapping.create': '직원 NFC 매핑 등록',
  'nfcMapping.delete': '직원 NFC 매핑 해제',
  // 보안
  'selfApproval.blocked': '자가승인 시도 차단',
  // 조직·관리
  'organization.create': '조직 생성',
  'drive.serviceAccountUpdate': 'Drive 서비스 계정 갱신',
}

const AUDIT_TARGET_TYPE_MAP: Record<string, string> = {
  employee: '직원',
  attendance: '출퇴근 기록',
  employeeDevice: '직원 기기',
  nfcSpot: 'NFC 태그판',
  employeeNfcSpot: '직원 NFC 매핑',
  organization: '조직',
  passwordResetToken: '비밀번호 리셋', // 폐기 도메인
}

export function auditActionLabel(action: string | null | undefined): string {
  if (!action) return ''
  return AUDIT_ACTION_MAP[action] ?? action
}

export function auditTargetTypeLabel(type: string | null | undefined): string {
  if (!type) return ''
  return AUDIT_TARGET_TYPE_MAP[type] ?? type
}
